import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import { createChannel } from "../../../services/channelService";

export default function CreateChannelModal({ onClose }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { usersData, refreshData } = useChat();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [step, setStep] = useState(1);
    const [isLaunching, setIsLaunching] = useState(false);
    const [inviteInput, setInviteInput] = useState("");
    const [invitedMembers, setInvitedMembers] = useState([]); // Stores user objects


    const handleCreate = () => {
        if (!name.trim()) return;
        setStep(2);
    };

    const handleLaunch = async () => {
        // 1. Prevent duplicate clicks
        if (!name.trim() || isLaunching) return;

        // Robust role extraction (handles Object or String formats)
        const getRawRole = () => {
            if (!user) return '';
            if (user.user_role && typeof user.user_role === 'object' && user.user_role.role) {
                return user.user_role.role;
            }
            if (typeof user.user_role === 'string') {
                return user.user_role;
            }
            return user.role || '';
        };

        const roleString = getRawRole().toLowerCase().trim();
        const isAdmin = roleString === 'admin' || roleString === 'superadmin';

        if (!isAdmin) {
            toast.error("Only administrators can create channels.");
            return;
        }

        setIsLaunching(true);
        const loadingToast = toast.loading("Creating and launching channel...");

        try {
            const memberIds = invitedMembers.map(m => m._id || m.id);
            const creatorId = user?._id || user?.id;

            if (creatorId && !memberIds.includes(creatorId)) {
                memberIds.push(creatorId);
            }

            const payload = {
                name,
                description,
                isPrivate,
                members: memberIds,
                active: true
            };

            // const response = await api.post("/channels", payload);
            const response = await createChannel(payload);


            if (response?.success) {
                const newChannelId = response.data?._id || response.data?.id;

                // ✅ SUCCESS: Atomic feedback
                toast.success("Channel created successfully!", { id: loadingToast });

                // Perform background sync without blocking the success feedback
                refreshData().catch(err => console.error("Post-creation refresh failed:", err));

                // Redirect immediately
                if (newChannelId) {
                    navigate(`/channel/${newChannelId}`);
                }

                onClose();
            } else {
                throw new Error(response.data?.message || "Failed to create channel");
            }

        } catch (error) {
            console.error("Failed to launch channel:", error);
            // ❌ FAILURE: Clear feedback
            toast.error(error.response?.data?.message || error.message || "Failed to create channel", { id: loadingToast });
        } finally {
            setIsLaunching(false);
        }
    };

    const addMember = (member) => {
        if (!invitedMembers.find(m =>
            (m._id || m.id) === (member._id || member.id)
        )) {
            setInvitedMembers((prev) => [...prev, member]);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-sm flex items-center justify-center px-4 animate-fade-in">
            <div className="w-full max-w-lg bg-app-surface border border-border-subtle rounded-3xl overflow-hidden shadow-modal flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle bg-app-bg/30">
                    <div>
                        <h2 className="text-xl font-bold text-text-primary tracking-tight">
                            {step === 1 ? "Create Channel" : "Add members"}
                        </h2>
                        <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mt-1 opacity-60">
                            {step === 1
                                ? "Building the digital foundation for your team."
                                : `Invite people to join #${name}`}
                        </p>
                    </div>

                    <button onClick={onClose} className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 border border-transparent transition-all shadow-subtle">
                        <AppIcon name="XMarkIcon" size={20} />
                    </button>
                </div>

                {/* Step Indicator */}
                <div className="flex px-6 py-1 gap-1 bg-app-bg/10">
                    {[1, 2].map((s) => (
                        <div
                            key={s}
                            className={`h-1 flex-1 rounded-full transition-all duration-500 ${s <= step ? "bg-primary shadow-[0_0_10px_rgba(var(--primary-rgb),0.4)]" : "bg-black/10"
                                }`}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="px-6 py-8 space-y-8">
                    {step === 1 ? (
                        <>
                            {/* Privacy */}
                            {/* <div className="flex gap-4">
                                <ChoiceButton
                                    active={!isPrivate}
                                    onClick={() => setIsPrivate(false)}
                                    icon="GlobeAltIcon"
                                    label="Public"
                                    desc="Anyone can join"
                                    disabled={isLaunching}
                                />
                                <ChoiceButton
                                    active={isPrivate}
                                    onClick={() => setIsPrivate(true)}
                                    icon="LockClosedIcon"
                                    label="Private"
                                    desc="Invite only"
                                    activeColor="text-emerald-500"
                                    disabled={isLaunching}
                                />
                            </div> */}

                            {/* Name */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest pl-1 opacity-60">Channel Name</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-text-muted group-focus-within:text-primary transition-colors">#</div>
                                    <input
                                        value={name}
                                        disabled={isLaunching}
                                        onChange={(e) =>
                                            setName(
                                                e.target.value
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")
                                                    .replace(/[^a-z0-9-]/g, "")
                                            )
                                        }
                                        className="w-full pl-10 pr-4 py-4 rounded-xl bg-app-surface border border-border-subtle focus:border-primary/50 text-text-primary font-medium outline-none transition-all placeholder:text-text-muted/30 shadow-subtle focus:ring-4 focus:ring-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="project-phoenix"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest pl-1 opacity-60">Description <span className="opacity-40 italic lowercase">(Optional)</span></label>
                                <textarea
                                    value={description}
                                    disabled={isLaunching}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-4 py-4 rounded-xl bg-app-surface border border-border-subtle focus:border-primary/50 text-text-primary font-medium resize-none outline-none transition-all shadow-subtle focus:ring-4 focus:ring-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    rows={3}
                                    placeholder="What is this channel about?"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Invite */}
                            <div className="space-y-6">
                                <div className="p-4 rounded-2xl bg-app-surface border border-border-subtle shadow-subtle focus-within:border-primary/50 transition-all">
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {invitedMembers.map((m) => (
                                            <span
                                                key={m._id || m.id}
                                                className="bg-primary/10 px-3 py-1.5 rounded-lg text-[11px] font-bold text-primary flex items-center gap-2 border border-primary/20 shadow-subtle"
                                            >
                                                {m.name || m.displayName}
                                                <button
                                                    onClick={() =>
                                                        setInvitedMembers((prev) =>
                                                            prev.filter((x) => (x._id || x.id) !== (m._id || m.id))
                                                        )
                                                    }
                                                    className="hover:text-red-500 transition-colors"
                                                >
                                                    <AppIcon name="XMarkIcon" size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <input
                                        value={inviteInput}
                                        disabled={isLaunching}
                                        onChange={(e) => setInviteInput(e.target.value)}
                                        className="w-full bg-transparent outline-none text-text-primary font-medium placeholder:text-text-muted/40 disabled:opacity-50"
                                        placeholder="Search for teammates..."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-text-muted uppercase tracking-widest pl-1 mb-2 block opacity-60">Suggestions</span>
                                    {usersData
                                        .filter((s) => !invitedMembers.find(m => (m._id || m.id) === (s._id || s.id)) &&
                                            (s.name || s.username || '').toLowerCase().includes(inviteInput.toLowerCase()) &&
                                            (s._id || s.id) !== (user?._id || user?.id) // Don't suggest ourselves
                                        )
                                        .slice(0, 5) // Limit to 5 for clean UI
                                        .map((s) => (
                                            <button
                                                key={s._id || s.id}
                                                onClick={() => addMember(s)}
                                                className="flex items-center gap-4 w-full text-left p-3.5 hover:bg-primary/5 rounded-2xl transition-all group border border-transparent hover:border-primary/10"
                                            >
                                                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-xs font-black text-primary border border-primary/20 group-hover:bg-primary group-hover:text-white transition-all shadow-subtle overflow-hidden">
                                                    {s.profilePhoto ? (
                                                        <img src={s.profilePhoto} className="w-full h-full object-cover" alt="" />
                                                    ) : (
                                                        (s.name || 'U').charAt(0)
                                                    )}
                                                </div>
                                                <span className="text-[14px] font-bold text-text-primary group-hover:text-primary transition-colors">{s.name || s.username}</span>
                                                <div className="ml-auto opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0">
                                                    <AppIcon name="PlusIcon" size={18} className="text-primary" />
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center px-6 py-5 bg-app-bg/50 border-t border-border-subtle">
                    <button
                        onClick={step === 1 ? onClose : () => setStep(1)}
                        className="text-[11px] font-bold text-text-muted hover:text-text-primary uppercase tracking-widest transition-colors flex items-center gap-2"
                    >
                        <AppIcon name={step === 1 ? "XMarkIcon" : "ArrowLeftIcon"} size={14} />
                        {step === 1 ? "Cancel" : "Back"}
                    </button>

                    <button
                        onClick={step === 1 ? handleCreate : handleLaunch}
                        disabled={(step === 1 && !name.trim()) || isLaunching}
                        className={`h-11 px-8 rounded-xl text-sm font-bold transition-all shadow-lg active:scale-95 flex items-center gap-2 ${(!name.trim() && step === 1) || isLaunching
                            ? "bg-black/5 text-text-muted/30 cursor-not-allowed"
                            : "bg-primary text-white shadow-primary/20 hover:bg-primary/90"
                            }`}
                    >
                        {isLaunching && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        {step === 1 ? "Next Step" : "Launch Channel"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ChoiceButton({ active, onClick, icon, label, desc, activeColor = "text-primary", disabled }) {
    return (
        <button
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            className={`flex-1 p-5 rounded-2xl border text-left transition-all duration-300 shadow-subtle ${active
                ? "border-primary bg-primary/10 shadow-primary/5 active:scale-[0.98]"
                : "border-border-subtle bg-app-surface hover:bg-black/5"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        >
            <AppIcon name={icon} size={28} className={`mb-4 transition-transform duration-300 ${active ? `${activeColor} scale-110` : "text-text-muted opacity-40 group-hover:opacity-100"}`} />
            <p className={`text-[15px] font-bold ${active ? "text-text-primary" : "text-text-muted"}`}>{label}</p>
            <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mt-1 opacity-60 leading-tight">{desc}</p>
        </button>
    );
}
